import React, { useMemo, useState } from 'react';
import ServicesConfirmation from './ServicesConfirmation';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/accordionContentStyle';
import { makeStyles } from '@material-ui/core';
import { BodyM } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import TermsAndConditions from './TermsAndConditions';
import SingleAccordionRow from './SingleAccordionRow';
import VatClause from 'components/VatClause/VatClause';
import Note from './Note';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IExtendedConfirmation extends types.services.IConfirmation {
	selectedElement: types.services.IPackage;
	header: string;
	dialogBody: string;
	action?: () => void;
}

interface IAccordionContentProps {
	elements: types.services.IPackage[];
	termsAndConditions?: types.services.termsAndConditions;
	updateService?: (singlePackage: types.services.IPackage, type?: types.services.actionType) => void;
	confirmations?: types.services.IConfirmation[];
	note?: string;
	notesOnTop?: boolean;
}

const AccordionContent = ({
	elements,
	termsAndConditions,
	updateService,
	confirmations = [],
	note,
	notesOnTop = false,
}: IAccordionContentProps) => {
	const classes = useStyles();
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
	const [confirmation, setConfirmation] = useState<IExtendedConfirmation | null>(null);

	const onClick = (element: types.services.IPackage, type: types.services.actionType, action?: () => void): void => {
		const basicConfirmationData = confirmations.find(
			(confirmation: types.services.IConfirmation) => confirmation.type === type
		);
		if (basicConfirmationData) {
			const dialogBody = translate(
				`number.services.default.${type.toLowerCase().replace('_', '.')}.confirmation`,
				element.name
			);
			const extendedConfirmationData: IExtendedConfirmation = {
				...basicConfirmationData,
				selectedElement: element,
				// default header when the custom one is not set
				header: basicConfirmationData.header || element.name,
				// default body when the custom one is not set
				dialogBody: basicConfirmationData.dialogBody || dialogBody,
				action,
			};
			setConfirmation(extendedConfirmationData);
			setConfirmationDialogOpen(true);
		} else {
			submit(element, action, type);
		}
	};

	const cancel = (): void => {
		setConfirmationDialogOpen(false);
		setConfirmation(null);
	};

	const submit = (element: types.services.IPackage, action?: () => void, type?: types.services.actionType): void => {
		setConfirmationDialogOpen(false);
		setConfirmation(null);
		if (!!action) {
			action();
		} else {
			updateService!(element, type);
		}
	};

	const numberOfButtons = useMemo(() => {
		return Math.max(...elements.map(({ buttons }) => buttons.length));
	}, [elements]);

	return (
		<div>
			<ConditionalRender show={!!note && !!notesOnTop}>
				<Note text={note} />
			</ConditionalRender>
			{elements.map((element) => (
				<SingleAccordionRow
					key={element.id}
					name={element.name}
					status={element.status}
					id={element.id}
					description={element.description}
					tooltip={element.tooltip}
					buttons={(element.buttons || []).map((singleButton) => ({
						...singleButton,
						onClick: () => onClick(element, singleButton.type, singleButton.action),
					}))}
					numberOfButtons={numberOfButtons}
				/>
			))}
			<ConditionalRender
				show={!elements || elements.length <= 0}
				onTrue={() => (
					<div className={classes.singleAccordionRow}>
						<div>
							<BodyM color="disabled-light" disableMargin>
								{translate('numbers.services.no.services.available')}
							</BodyM>
						</div>
					</div>
				)}
			/>
			<div className={classes.vatClauseWrapper}>
				<VatClause type={'one-line'} />
			</div>
			<ConditionalRender
				show={!!termsAndConditions}
				onTrue={() => <TermsAndConditions termsAndConditions={termsAndConditions!} />}
			/>
			<ConditionalRender
				show={!!confirmation}
				onTrue={() => (
					<ServicesConfirmation
						isOpen={confirmationDialogOpen}
						cancel={cancel}
						submit={() => submit(confirmation!.selectedElement, confirmation!.action)}
						type={confirmation!.type}
						dialogBody={confirmation!.dialogBody}
						header={confirmation!.header}
					/>
				)}
			/>
		</div>
	);
};

export default AccordionContent;
